import {
  Badge,
  type BaseField,
  buildForm,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  Input,
  Label,
} from "@bleu.builders/ui";
import { AlertCircle, CheckCircle, Clock, XCircle } from "lucide-react";
// biome-ignore lint/style/useImportType: <explanation>
import React, { useMemo } from "react";

interface StatusConfig {
  label: string;
  color: string;
  icon: React.ReactNode;
}

import Link from "@components/Link";
import { useUser } from "@contexts/UserContext";
import { canManageProgram } from "@lib/permissions";
import { formatDate } from "@utils/formatDate";
import { cn } from "@utils/mergeClassNames";

import { useReceiptUpdate } from "./context";

const fields = (rejectOptions, merchantOptions) =>
  [
    {
      name: "status",
      label: "Status",
      description: "",
      type: "hidden",
    },
    {
      name: "purchase_proof_merchant_id",
      label: "Merchant Name",
      description: "",
      type: "select",
      options: [...merchantOptions, { label: "None", value: "null" }],
    },
    {
      name: "merchant_address",
      label: "Merchant Address",
      description: "",
      type: "input",
    },
    {
      name: "purchase_date",
      label: "Purchase Date",
      description: "",
      type: "datetime",
    },
    {
      name: "integrity",
      label: "Receipt integrity",
      description:
        "mark this after reviewing the items, if valid, the status will be moved to processed and the user will receive the points. if invalid, will be moved to processed and no points will be given.",
      type: "select",
      options: [
        { value: "valid", label: "Valid" },
        { value: "invalid", label: "Invalid" },
      ],
    },
    {
      name: "admin_invalidation_reason",
      label: "Invalidation Reason",
      description: "",
      type: "select",
      options: rejectOptions,
      conditions: {
        allOf: [{ integrity: ["invalid"] }],
      },
    },
    {
      name: "admin_invalidation_message",
      label: "Admin Feedback Message",
      description: "",
      type: "wysiwyg",
      conditions: {
        allOf: [{ integrity: ["invalid"] }],
      },
    },
    {
      name: "admin_comments",
      label: "Admin Comments",
      description: "",
      type: "textarea",
    },
  ] as BaseField[];

type ReceiptStatus =
  | "pending"
  | "processed"
  | "eligible"
  | "no_eligible_items"
  | "needs_human_review"
  | "rejected"
  | "failed"
  | "review_requested";
interface StatusConfig {
  label: string;
  color: string;
  icon: React.ReactNode;
}

const statusConfig: Record<ReceiptStatus, StatusConfig> = {
  pending: {
    label:
      "This receipt is being processed by the system, we recommend to avoid updating the data for now",
    color: "bg-primary text-primary-foreground",
    icon: <Clock className="size-4" />,
  },
  failed: {
    label:
      "This receipt has failed the processing, the development team has been notified and will work on a fix",
    color: "bg-destructive text-destructive-foreground",
    icon: <XCircle className="size-4" />,
  },
  processed: {
    label:
      "This receipt has been processed with success and points will be given",
    color: "bg-green-700",
    icon: <CheckCircle className="size-4" />,
  },
  eligible: {
    label: "This receipt has been processed with success and points were given",
    color: "bg-green-700",
    icon: <CheckCircle className="size-4" />,
  },
  no_eligible_items: {
    label: "This receipt was processed and has no eligible items",
    color: "bg-yellow-600 text-white font-semibold",
    icon: <AlertCircle className="size-4" />,
  },
  needs_human_review: {
    label: "Needs Admin Verification",
    color: "bg-red-600 text-white font-semibold",
    icon: <AlertCircle className="size-4" />,
  },
  rejected: {
    label: "Rejected",
    color: "bg-destructive text-destructive-foreground",
    icon: <XCircle className="size-4" />,
  },
  review_requested: {
    label: "Participant requested review",
    color: "bg-red-600 text-white font-semibold",
    icon: <AlertCircle className="size-4" />,
  },
};

const TaggedReason = ({ tag }) => {
  switch (tag.name) {
    case "duplicated":
      return (
        <>
          <span>Receipt with similar metadata to receipts</span>
          {tag.linked_data.similar_receipt_ids.slice(-5).map((id, index) => (
            <Link
              key={id}
              to={`/admin/v2/receipts/${id}`}
              target="_blank"
              className="ml-1 font-medium text-amber-900 hover:text-amber-700"
            >
              #{id}
            </Link>
          ))}
        </>
      );
    default:
      return tag.reason;
  }
};

const ISSUE_REPORT_MAP = {
  item_not_recognized:
    "Participant reported receipt issue: Item was not recognized",
  incorrect_points_awarded:
    "Participant reported receipt issue: Incorrect Points",
  wrong_item_categorized: "Participant reported receipt issue: Wrong Category",
};

export function ReceiptDetailsCard() {
  const { form, receipt, rejectReasonsOptions, merchantOptions } =
    useReceiptUpdate();
  const { permissions } = useUser();
  const status = receipt ? receipt["status"] : "pending";

  const openReceiptReportedIssues = receipt?.open_receipt_issue_reports;

  const formFields = useMemo(
    () => fields(rejectReasonsOptions, merchantOptions),
    [rejectReasonsOptions, merchantOptions],
  );

  const admin_marked_as_valid_at = receipt?.admin_marked_as_valid_at;
  const admin_marked_as_invalid_at = receipt?.admin_marked_as_invalid_at;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Receipt Overview</CardTitle>
        <CardDescription>Review and edit receipt details</CardDescription>
      </CardHeader>
      <Form {...form}>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-2 items-center">
              {receipt?.source === "external" && (
                <Badge
                  variant="primary"
                  className="px-3 py-1.5 text-sm bg-green-500"
                >
                  <span className="flex items-center">
                    <CheckCircle className="size-4" />
                    <span className="ml-2">
                      This receipt was created from an API request
                    </span>
                  </span>
                </Badge>
              )}
              <Badge
                variant="primary"
                className={cn(
                  "px-3 py-1.5 text-sm",
                  statusConfig[status].color,
                )}
              >
                <span className="flex items-center">
                  {statusConfig[status].icon}
                  <span className="ml-2">{statusConfig[status].label}</span>
                </span>
              </Badge>
              {admin_marked_as_valid_at && (
                <Badge
                  variant="primary"
                  className={cn(
                    "px-3 py-1.5 text-sm !mt-0",
                    statusConfig[status].color,
                  )}
                >
                  <span className="flex items-center whitespace-nowrap">
                    <span className="font-bold">Admin marked as valid at:</span>{" "}
                    {formatDate(admin_marked_as_valid_at)}
                  </span>
                </Badge>
              )}

              {admin_marked_as_invalid_at && (
                <Badge
                  variant="primary"
                  className={cn(
                    "px-3 py-1.5 text-sm !mt-0",
                    statusConfig[status].color,
                  )}
                >
                  <span className="flex items-center whitespace-nowrap">
                    <span className="font-bold">
                      Admin marked as invalid at:
                    </span>{" "}
                    {formatDate(admin_marked_as_invalid_at)}
                  </span>
                </Badge>
              )}
            </div>

            {["needs_human_review", "rejected"].includes(status) &&
              receipt?.receipt_tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {receipt.receipt_tags.map((tag, index) => (
                    <div
                      key={index}
                      className={cn(
                        "inline-flex items-center rounded-full border border-amber-500/50 bg-amber-50 px-3 py-1 text-sm text-amber-900",
                        {
                          "border-red-500/50 bg-red-50 text-red-900":
                            status === "rejected" &&
                            //@ts-expect-error linked_data is an object without defined attributes
                            tag.linked_data?.status === "rejected",
                        },
                      )}
                    >
                      <TaggedReason tag={tag} />
                    </div>
                  ))}
                </div>
              )}
            {openReceiptReportedIssues?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {openReceiptReportedIssues.map((issue, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center rounded-full border px-3 py-1 text-sm border-red-500/50 bg-red-50 text-red-900"
                  >
                    {ISSUE_REPORT_MAP[issue]}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            {buildForm(formFields, form)}
            <div className="grid sm:grid-cols-3 gap-4 w-full">
              <div>
                <Label htmlFor="totalAmount">Total Amount</Label>
                <Input
                  id="totalAmount"
                  value={receipt ? receipt["total_amount"] : ""}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="totalItems">Total Items</Label>
                <Input
                  id="totalItems"
                  value={receipt ? receipt["items_count"] : ""}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="Id">ID</Label>
                <Input id="Id" value={receipt ? receipt["id"] : ""} disabled />
              </div>
              <div className="flex flex-col gap-4">
                <Label htmlFor="participant-id">Participant ID</Label>
                {canManageProgram(permissions) ? (
                  <Link
                    id="participant-id"
                    className="text-blue-500 underline"
                    to={`/admin/v2/programs/${receipt?.program_id}/users/${receipt?.participant_id}`}
                  >
                    {receipt ? receipt["participant_id"] : ""}
                  </Link>
                ) : (
                  <Input
                    id="participant-id"
                    value={receipt ? receipt["participant_id"] : ""}
                    disabled
                  />
                )}
              </div>
              <div>
                <Label htmlFor="participant-points">Participant Points</Label>
                <Input
                  id="participant-points"
                  value={receipt ? receipt["participant_points"] : ""}
                  disabled
                />
              </div>
              <div>
                <Label htmlFor="participant-completed-challenges">
                  Participant Completed Challenges
                </Label>
                <Input
                  id="participant-completed-challenges"
                  value={
                    receipt ? receipt["participant_completed_challenges"] : ""
                  }
                  disabled
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Form>
    </Card>
  );
}
